import React, { useState, useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { useMyActivity, useMyCommits, useMyRepos, useMyPullRequests, useTeamActivity, useActivityLeaderboard } from '../api/activityApi';
import { useConnectionStatus, useInitiateOAuth, useDisconnect, useSyncActivity } from '../api/bitbucketOAuthApi';
import { FiGitCommit, FiGitPullRequest, FiCode, FiFolder, FiTrendingUp, FiCalendar, FiAward, FiGithub, FiCheckCircle, FiXCircle, FiUsers } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { COLORS as C, RADIUS } from '../constants/ui';

const COLORS = [C.accent, C.done, C.warn, C.danger, '#8b5cf6', '#06b6d4'];

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to group commits by time period
const groupCommitsByPeriod = (commits) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: []
  };
  
  commits.forEach(commit => {
    const commitDate = new Date(commit.commit_date);
    const commitDay = new Date(commitDate.getFullYear(), commitDate.getMonth(), commitDate.getDate());
    
    if (commitDay.getTime() === today.getTime()) {
      groups.today.push(commit);
    } else if (commitDay.getTime() === yesterday.getTime()) {
      groups.yesterday.push(commit);
    } else {
      groups.thisWeek.push(commit);
    }
  });
  
  return groups;
};

const DeveloperActivity = () => {
  const { role } = useContext(AuthContext);
  const currentRole = (role || '').toLowerCase();
  const canViewTeam = ['superadmin', 'admin', 'manager', 'analyst', 'product', 'product_manager', 'lead'].includes(currentRole);
  const isManagerRole = ['superadmin', 'admin', 'manager', 'analyst', 'product', 'product_manager', 'lead'].includes(currentRole);

  // Auto-redirect managers to Team Activity page
  React.useEffect(() => {
    if (isManagerRole) {
      window.location.href = '/team-activity';
    }
  }, [isManagerRole]);

  const [dateRange, setDateRange] = useState('week');
  const [commitsPage, setCommitsPage] = useState(0);
  const commitsLimit = 20;

  // OAuth connection hooks (only for non-manager roles)
  const { data: connectionStatus, isLoading: connectionLoading } = useConnectionStatus();
  const initiateOAuth = useInitiateOAuth();
  const disconnect = useDisconnect();
  const syncActivity = useSyncActivity();
  
  console.log('🔍 Sync loading state:', syncActivity.isPending || syncActivity.isLoading);

  const isConnected = connectionStatus?.connected || false;

  const { data: activityData, isLoading: activityLoading } = useMyActivity(dateRange);
  const { data: commitsData, isLoading: commitsLoading } = useMyCommits(50, 0);
  const { data: reposData, isLoading: reposLoading } = useMyRepos();
  const { data: prsData = [], isLoading: prsLoading } = useMyPullRequests(dateRange);
  const { data: teamActivity, isLoading: teamLoading } = useTeamActivity(dateRange);
  const { data: leaderboard, isLoading: leaderboardLoading } = useActivityLeaderboard(dateRange, 10);

  const summary = activityData?.summary || {};
  const dailyActivity = activityData?.daily_activity || [];
  const repoActivity = activityData?.repo_activity || [];
  const commits = commitsData?.commits || [];
  const repositories = reposData?.repositories || [];
  const teamData = teamActivity?.team_activity || [];
  const leaderboardData = leaderboard?.leaderboard || [];

  const totalPages = Math.ceil((commitsData?.total || 0) / 50);

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const StatCard = ({ icon: Icon, label, value, color, subtitle }) => (
    <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: `2px solid ${color}20` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ background: `${color}15`, padding: '0.75rem', borderRadius: '8px' }}>
          <Icon size={24} color={color} />
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 700, color }}>{formatNumber(value)}</div>
      </div>
      <div style={{ fontSize: '0.9rem', color: C.inkSoft, fontWeight: 500 }}>{label}</div>
      {subtitle && <div style={{ fontSize: '0.8rem', color: C.inkFaint, marginTop: '0.25rem' }}>{subtitle}</div>}
    </div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <FiTrendingUp /> Developer Activity
        </h1>
        <p style={{ color: C.inkSoft }}>Track your commits, pull requests, and code contributions</p>
      </div>

      {/* OAuth Connection Banner - Only for Developers */}
      {!isManagerRole && !connectionLoading && (
        <div style={{
          background: isConnected 
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            {isConnected ? (
              <>
                <FiCheckCircle size={32} color="white" />
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>Bitbucket Connected</h3>
                  <p style={{ margin: '0.25rem 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                    Connected as {connectionStatus?.username || 'User'} • Last synced: {connectionStatus?.last_synced ? new Date(connectionStatus.last_synced).toLocaleString() : 'Never'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <FiXCircle size={32} color="white" />
                <div>
                  <h3 style={{ margin: 0, color: '#92400e', fontSize: '1.1rem' }}>Bitbucket Not Connected</h3>
                  <p style={{ margin: '0.25rem 0 0', color: '#78350f', fontSize: '0.9rem' }}>
                    Connect your Bitbucket account to track your commits and pull requests (last 1 week)
                  </p>
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {isConnected ? (
              <button
                onClick={() => syncActivity.mutate()}
                disabled={syncActivity.isPending}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: syncActivity.isPending ? C.inkFaint : C.accent,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: syncActivity.isPending ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: syncActivity.isPending ? 0.7 : 1
                }}
              >
                {syncActivity.isPending ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Syncing...
                  </>
                ) : (
                  <>
                    🔄 Sync Now
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => initiateOAuth.mutate()}
                disabled={initiateOAuth.isPending}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: initiateOAuth.isPending ? C.inkFaint : C.accent,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: initiateOAuth.isPending ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: initiateOAuth.isPending ? 0.6 : 1
                }}
              >
                {initiateOAuth.isPending ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <FiGithub size={20} />
                    Connect Bitbucket Account
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Manager/Admin Redirect Message */}
      {isManagerRole && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
          color: 'white',
          border: '3px solid #fbbf24'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '1rem',
              borderRadius: '12px'
            }}>
              <FiUsers size={40} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>Manager/Admin Notice</h3>
              <p style={{ margin: '0.5rem 0 0', fontSize: '1rem', lineHeight: '1.5' }}>
                <strong>This page shows YOUR personal activity only.</strong><br/>
                To view all team members' commits, code, and repos, click the button below.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/team-activity'}
            style={{
              padding: '1rem 2rem',
              background: 'white',
              color: C.warn,
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            <FiUsers size={24} />
            View Team Activity Dashboard →
          </button>
        </div>
      )}

      {/* Date Range Selector */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <FiCalendar color={C.inkSoft} />
          <span style={{ fontWeight: 500, marginRight: '0.5rem' }}>Time Period:</span>
          {['yesterday', 'today', 'week', 'month', 'quarter'].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              style={{
                padding: '0.5rem 1rem',
                background: dateRange === range ? C.accent : C.sunken,
                color: dateRange === range ? 'white' : C.inkSoft,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                textTransform: 'capitalize'
              }}
            >
              {range === 'yesterday' ? 'Yesterday' : range === 'today' ? 'Today' : `This ${range.charAt(0).toUpperCase() + range.slice(1)}`}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard
          icon={FiGitCommit}
          label="Total Commits"
          value={summary.total_commits || 0}
          color={C.accent}
          subtitle={`${summary.active_days || 0} active days`}
        />
        <StatCard
          icon={FiCode}
          label="Lines Added"
          value={summary.total_lines_added || 0}
          color={C.done}
          subtitle={`${formatNumber(summary.total_lines_deleted || 0)} deleted`}
        />
        <StatCard
          icon={FiGitPullRequest}
          label="Pull Requests"
          value={summary.total_prs_created || 0}
          color="#8b5cf6"
          subtitle={`${summary.total_prs_merged || 0} merged`}
        />
        <StatCard
          icon={FiFolder}
          label="Active Repos"
          value={summary.active_repos || 0}
          color={C.warn}
        />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(500px, 100%), 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Daily Commit Trend */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiGitCommit color={C.accent} /> Daily Commit Activity
          </h3>
          {dailyActivity.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
                <XAxis dataKey="activity_date" tickFormatter={formatDate} stroke={C.inkSoft} fontSize={12} />
                <YAxis stroke={C.inkSoft} fontSize={12} />
                <Tooltip
                  contentStyle={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '6px' }}
                  labelFormatter={formatDate}
                />
                <Legend />
                <Line type="monotone" dataKey="commits" stroke={C.accent} strokeWidth={2} name="Commits" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: C.inkFaint }}>No activity data available</div>
          )}
        </div>

        {/* Lines of Code Chart */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiCode color={C.done} /> Lines of Code
          </h3>
          {dailyActivity.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyActivity}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
                  <XAxis dataKey="activity_date" tickFormatter={formatDate} stroke={C.inkSoft} fontSize={12} />
                  <YAxis stroke={C.inkSoft} fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '6px' }}
                    labelFormatter={formatDate}
                  />
                  <Legend />
                  <Bar dataKey="lines_added" fill={C.done} name="Added" />
                  <Bar dataKey="lines_deleted" fill={C.danger} name="Deleted" />
                </BarChart>
              </ResponsiveContainer>
              {repoActivity.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '1rem', background: C.sunken, borderRadius: '6px' }}>
                  <p style={{ fontSize: '0.85rem', color: C.inkSoft, marginBottom: '0.5rem', fontWeight: 600 }}>
                    Active Repositories ({repoActivity.length}):
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {repoActivity.slice(0, 10).map((repo, idx) => (
                      <span key={idx} style={{
                        padding: '0.25rem 0.75rem',
                        background: 'white',
                        border: `1px solid ${C.line}`,
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: C.inkSoft
                      }}>
                        {repo.repo_name} ({repo.commits})
                      </span>
                    ))}
                    {repoActivity.length > 10 && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: C.line,
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: C.inkSoft
                      }}>
                        +{repoActivity.length - 10} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: C.inkFaint }}>No code data available</div>
          )}
        </div>
      </div>

      {/* Repository Distribution */}
      {repoActivity.length > 0 && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiFolder color={C.warn} /> Repository Distribution
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={repoActivity.slice(0, 6)}
                  dataKey="commits"
                  nameKey="repo_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.repo_name}: ${entry.commits}`}
                >
                  {repoActivity.slice(0, 6).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div>
              <table style={{ width: '100%', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.line}` }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Repository</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Commits</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Lines Added</th>
                  </tr>
                </thead>
                <tbody>
                  {repoActivity.map((repo, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${C.sunken}` }}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>{repo.repo_name}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{repo.commits}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: C.done }}>+{formatNumber(repo.lines_added)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {/* Recent Commits */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FiGitCommit color={C.accent} /> Recent Commits
        </h3>
        {commits.length > 0 ? (
          <CommitsGrouped commits={commits} />
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: C.inkFaint }}>No commits found</div>
        )}
      </div>

    </div>
  );
};

// Commits Grouped Component
const CommitsGrouped = ({ commits }) => {
  const grouped = groupCommitsByPeriod(commits);
  
  const renderCommitTable = (commitList) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: C.sunken, borderBottom: `2px solid ${C.line}` }}>
          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Message</th>
          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Repository</th>
          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
          <th style={{ padding: '0.75rem', textAlign: 'right' }}>Changes</th>
        </tr>
      </thead>
      <tbody>
        {commitList.map((commit, idx) => (
          <tr 
            key={idx} 
            onClick={() => commit.commit_url && window.open(commit.commit_url, '_blank')}
            style={{ 
              borderBottom: `1px solid ${C.line}`,
              cursor: commit.commit_url ? 'pointer' : 'default',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => commit.commit_url && (e.currentTarget.style.background = C.sunken)}
            onMouseLeave={(e) => commit.commit_url && (e.currentTarget.style.background = 'white')}
          >
            <td style={{ padding: '0.75rem', maxWidth: '400px' }}>
              <div style={{ fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: commit.commit_url ? C.accent : 'inherit' }}>
                {commit.commit_message?.split('\n')[0] || 'No message'}
              </div>
              <div style={{ fontSize: '0.75rem', color: C.inkFaint, fontFamily: 'monospace' }}>
                {commit.commit_hash?.substring(0, 7)}
              </div>
            </td>
            <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{commit.repo_name}</td>
            <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: C.inkSoft }}>
              {formatDate(commit.commit_date)}
            </td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem' }}>
              <span style={{ color: C.done, marginRight: '0.5rem' }}>+{commit.lines_added}</span>
              <span style={{ color: C.danger }}>-{commit.lines_deleted}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {grouped.today.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: C.inkSoft, marginBottom: '0.75rem', paddingLeft: '0.5rem', borderLeft: `3px solid ${C.done}` }}>
            Today ({grouped.today.length})
          </h4>
          {renderCommitTable(grouped.today)}
        </div>
      )}
      
      {grouped.yesterday.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: C.inkSoft, marginBottom: '0.75rem', paddingLeft: '0.5rem', borderLeft: `3px solid ${C.warn}` }}>
            Yesterday ({grouped.yesterday.length})
          </h4>
          {renderCommitTable(grouped.yesterday)}
        </div>
      )}
      
      {grouped.thisWeek.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: C.inkSoft, marginBottom: '0.75rem', paddingLeft: '0.5rem', borderLeft: `3px solid ${C.accent}` }}>
            This Week ({grouped.thisWeek.length})
          </h4>
          {renderCommitTable(grouped.thisWeek)}
        </div>
      )}
    </div>
  );
};

export default DeveloperActivity;

// Add spinner animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);
